// pages/EventDetails.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Avatar,
  AvatarGroup,
  IconButton,
  Divider,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/system";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaidIcon from "@mui/icons-material/Paid";
import PeopleIcon from "@mui/icons-material/People";
import ShareIcon from "@mui/icons-material/Share";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import GroupsIcon from "@mui/icons-material/Groups";
import CategoryIcon from "@mui/icons-material/Category";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Event as EventIcon
} from "@mui/icons-material";
import api from "../../api";
import { getFullImageUrl } from "../../middleware/Imagegetter";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton"; // Added BackButton import

// Styled Components
const HeroContainer = styled(Box)({
  position: "relative",
  minHeight: "400px",
  display: "flex",
  alignItems: "flex-end",
  color: "white",
  overflow: "hidden",
  borderRadius: "24px",
  border: "1px solid rgba(13, 42, 68, 0.12)",
  boxShadow: "0 18px 40px rgba(0, 0, 0, 0.16)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7))",
    zIndex: 1,
  },
});

const EventImage = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const ContentCard = styled(Paper)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  borderRadius: "24px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  padding: theme.spacing(3),
  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
  height: "100%",
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  background: "rgba(102, 126, 234, 0.05)",
  borderRadius: "16px",
  marginBottom: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ variant, theme }) => ({
  borderRadius: "50px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 32px",
  fontSize: "1rem",
  transition: "all 0.3s ease",
  ...(variant === "primary" && {
    background:
      "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
    color: "white",
    boxShadow: "0 10px 25px rgba(102, 126, 234, 0.3)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 15px 35px rgba(102, 126, 234, 0.4)",
    },
  }),
  ...(variant === "secondary" && {
    border: "2px solid #667eea",
    color: "#667eea",
    "&:hover": {
      background: "rgba(102, 126, 234, 0.05)",
      borderWidth: "2px",
    },
  }),
}));

const THEME_BLUE = "#0d2a44";

function formatDateDDMMYYYY(value) {
  if (!value) return "";
  const raw = String(value);
  const datePart = raw.includes("T") ? raw.split("T")[0] : raw;

  // If already DD-MM-YYYY, keep it.
  if (/^\d{2}-\d{2}-\d{4}$/.test(datePart)) return datePart;

  // Handle YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [yyyy, mm, dd] = datePart.split("-");
    return `${dd}-${mm}-${yyyy}`;
  }

  // Fallback: try Date parsing.
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = String(parsed.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

function formatTimeToHours(value) {
  if (!value) return "";
  const raw = String(value).trim();

  // Handle already-12h formats like: 1 PM, 1:30pm
  const ampmMatch = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
  if (ampmMatch) {
    const hours = Number(ampmMatch[1]);
    const minutes = ampmMatch[2] ?? "00";
    const suffix = ampmMatch[3].toUpperCase();
    if (!Number.isFinite(hours) || hours < 1 || hours > 12) return raw;

    return minutes === "00" ? `${hours} ${suffix}` : `${hours}:${minutes} ${suffix}`;
  }

  // Handle HH:MM or HH:MM:SS (24h)
  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    const hours24 = Number(match[1]);
    const minutes = match[2];
    if (!Number.isFinite(hours24)) return raw;

    const hours12 = ((hours24 % 12) || 12);
    const suffix = hours24 >= 12 ? "PM" : "AM";
    // User asked for 1,2,3... format: drop :00
    return minutes === "00" ? `${hours12} ${suffix}` : `${hours12}:${minutes} ${suffix}`;
  }

  return raw;
}

function XBrandIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M18.146 2H21l-6.49 7.418L22.5 22h-6.67l-5.22-7.503L3.81 22H1l7.02-8.021L0.5 2h6.74l4.71 6.836L18.146 2Zm-1.17 18h1.85L7.86 3.93H5.88L16.976 20Z" />
    </SvgIcon>
  );
}

export default function EventDetails({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [event, setEvent] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [loading, setLoading] = useState(true);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Sidebar items
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />
    },
  ];

  useEffect(() => {
    const fetchevent = async () => {
      try {
        setLoading(true);
        const response = await api.get(`events/event/${id}`);
        const eventData = response?.data || response?.event || response;
        setEvent(eventData);
      } catch (error) {
        // console.log("Error fetching event", error);
        setSnackbarMessage("Failed to load event details");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchevent();
  }, [id]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: event.details,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setSnackbarMessage("Link copied to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };

  const handleSharePlatform = (platform) => {
    const url = window.location.href;
    const encodedUrl = encodeURIComponent(url);
    const title = event?.title || "Event";
    const encodedText = encodeURIComponent(`${title} - ${url}`);

    if (platform === "email") {
      window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`;
      return;
    }

    const shareUrl =
      platform === "facebook"
        ? `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
        : platform === "x"
          ? `https://twitter.com/intent/tweet?text=${encodedText}`
          : platform === "whatsapp"
            ? `https://wa.me/?text=${encodedText}`
            : null;

    if (shareUrl) {
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    setSnackbarMessage(!bookmarked ? "Event bookmarked!" : "Bookmark removed");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const embeddedRole = (() => {
    const path = String(location?.pathname || '').toLowerCase();
    if (path.startsWith('/clubmember/')) return 'club_member';
    if (path.startsWith('/student/')) return 'student';
    return null;
  })();

  const getEventDetailsPath = (eventId) => {
    if (embeddedRole === 'club_member') return `/clubmember/event/${eventId}`;
    if (embeddedRole === 'student') return `/student/event/${eventId}`;
    return `/event/${eventId}`;
  };

  const handleBackToEvents = () => {
    if (!embedded) {
      navigate(-1);
      return;
    }

    if (embeddedRole === 'club_member') {
      navigate('/clubmember/events');
      return;
    }

    if (embeddedRole === 'student') {
      navigate('/student/events');
      return;
    }

    navigate('/events');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: embedded ? "auto" : "100vh",
          py: embedded ? 6 : 0,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} sx={{ color: "#667eea", mb: 2 }} />
          <Typography variant="h6" sx={{ color: "#333" }}>Loading event details...</Typography>
        </Box>
      </Box>
    );
  }

  if (!event) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: embedded ? "auto" : "100vh",
          background: embedded ? "transparent" : "#f8f9ff",
        }}
      >
        {!embedded ? <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} /> : null}
        <Box sx={{ display: "flex", flexGrow: 1 }}>
          {!embedded && !isMobile ? <GlobalSidebar /> : null}
          
          <Box component="main" sx={{ flexGrow: 1, overflowX: "hidden" }}>
            <Container maxWidth="lg" sx={{ py: embedded ? 2 : 8, textAlign: "center" }}>
              <Typography variant="h4" sx={{ mb: 2, color: "#333" }}>
                Event not found
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: "#666" }}>
                The event you're looking for doesn't exist or has been removed.
              </Typography>
              <ActionButton
                variant="secondary"
                onClick={handleBackToEvents}
                startIcon={<ArrowBackIcon />}
              >
                Back to Events
              </ActionButton>
            </Container>
          </Box>
        </Box>
      </Box>
    );
  }

  // Calculate attendance percentage
  const attendancePercentage = event.capacity 
    ? Math.round(((event.participants || 0) / event.capacity) * 100)
    : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: embedded ? "auto" : "100vh",
        background: embedded ? "transparent" : "#f8f9ff",
      }}
    >
      {!embedded ? <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} /> : null}
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {!embedded && !isMobile ? <GlobalSidebar /> : null}
        
        <Box component="main" sx={{ flexGrow: 1, overflowX: "hidden" }}>
          {/* Hero Section with Image */}
          <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 3 } }}>
            <HeroContainer>
              <EventImage>
                <img 
                  src={getFullImageUrl(event.image) || "https://source.unsplash.com/random/1600x400/?event,campus,conference"} 
                  alt={event.title}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://source.unsplash.com/random/1600x400/?event";
                  }}
                />
              </EventImage>

              <Box sx={{ position: "relative", zIndex: 2, pb: 4, pt: { xs: 2, md: 3 }, px: { xs: 2, md: 4 } }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToEvents}
                sx={{
                  color: "white",
                  textTransform: "none",
                  mb: 3,
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "50px",
                  px: 3,
                  "&:hover": {
                    background: "rgba(255,255,255,0.2)",
                  },
                }}
              >
                Back to Events
              </Button>

              <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start", 
                flexWrap: "wrap", 
                gap: 3 
              }}>
                <Box sx={{ flex: 1, minWidth: "300px" }}>
                  <Chip
                    label={event.type}
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      color: "white",
                      fontWeight: 600,
                      mb: 2,
                      textTransform: "capitalize",
                    }}
                  />
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      mb: 2,
                      fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                      textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                    }}
                  >
                    {event.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      opacity: 0.9,
                      fontWeight: 300,
                      maxWidth: "800px",
                      textShadow: "0 1px 5px rgba(0,0,0,0.3)",
                    }}
                  >
                    {event.ai_summary || event.details?.substring(0, 200)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <IconButton
                    onClick={handleShare}
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      color: "white",
                      "&:hover": {
                        background: "rgba(255,255,255,0.3)",
                      },
                    }}
                  >
                    <ShareIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleBookmark}
                    sx={{
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                      color: bookmarked ? "#FFD700" : "white",
                      "&:hover": {
                        background: "rgba(255,255,255,0.3)",
                      },
                    }}
                  >
                    {bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  </IconButton>
                </Box>
              </Box>
              </Box>
            </HeroContainer>
          </Container>

          {/* Main Content */}
          <Container maxWidth="lg" sx={{ py: 6 }}>
            <Grid container spacing={4}>
              {/* Left Column - Event Details */}
              <Grid item xs={12} md={8}>
                <ContentCard>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: "#333" }}>
                    About this Event
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    color: "#555", 
                    lineHeight: 1.8,
                    mb: 4,
                    fontSize: "1.1rem"
                  }}>
                    {event.details}
                  </Typography>

                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: "#333" }}>
                    Event Information
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <CalendarMonthIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                            {formatDateDDMMYYYY(event.date)}
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <AccessTimeIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Time
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                            {event.time ? formatTimeToHours(event.time) : "To be announced"}
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <LocationOnIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                            {event.location}
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <CategoryIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Category
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333", textTransform: "capitalize" }}>
                            {event.type}
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <GroupsIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Organizer
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                            {event.club}
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <InfoItem>
                        <PeopleIcon sx={{ color: THEME_BLUE }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                            Participants
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                            {event.participants || 0} registered
                          </Typography>
                        </Box>
                      </InfoItem>
                    </Grid>
                  </Grid>

                  {/* Tags */}
                  {event.tags && event.tags.length > 0 && (
                    <>
                      <Divider sx={{ my: 4 }} />
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#333" }}>
                        Tags
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {event.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{
                              background: "rgba(102, 126, 234, 0.1)",
                              color: "#667eea",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </ContentCard>
              </Grid>

              {/* Right Column - Registration & Side Info */}
              <Grid item xs={12} md={4}>
                <ContentCard sx={{ position: embedded ? "static" : "sticky", top: embedded ? "auto" : "100px" }}>
                  {/* Status Badge */}
                  <Chip
                    label={`Registration ${event.status}`}
                    size="large"
                    sx={{
                      background: event.status === 'open' 
                        ? "linear-gradient(135deg, #016f57 30%, #016f57 70%, #016f57 100%)"
                        : "linear-gradient(45deg, #f44336, #c62828)",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "1rem",
                      width: "100%",
                      mb: 3,
                      py: 1.5,
                    }}
                  />

                  {/* Price */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <PaidIcon sx={{ color: "#FF9800" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#333" }}>
                        Price
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#333" }}>
                      {event.price || "Free"}
                    </Typography>
                  </Box>

                  {/* Attendance Progress */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        Seats filled
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#667eea", fontWeight: 600 }}>
                        {attendancePercentage}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={attendancePercentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        background: "rgba(102, 126, 234, 0.1)",
                        "& .MuiLinearProgress-bar": {
                          background: event.status === 'open' 
                            ? "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)"
                            : "linear-gradient(45deg, #f44336, #c62828)",
                          borderRadius: 5,
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: "#666", mt: 0.5, display: "block" }}>
                      {event.participants || 0} of {event.capacity || 100} seats
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
                    {event.status === 'open' ? (
                      <>
                        <ActionButton
                          variant="primary"
                          href={event.link}
                          target="_blank"
                          fullWidth
                        >
                          Register Now
                        </ActionButton>
                        <ActionButton
                          variant="THEME_BLUE"
                          sx={{ background: "rgba(13, 42, 68, 0.08)", color: THEME_BLUE }}
                          onClick={() => {
                            // Add to calendar logic
                            setSnackbarMessage("Added to your calendar!");
                            setSnackbarSeverity("success");
                            setSnackbarOpen(true);
                          }}
                          fullWidth
                        >
                          Add to Calendar
                        </ActionButton>
                      </>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: "12px" }}>
                        Registration for this event is closed.
                      </Alert>
                    )}
                  </Box>

                  {/* Participants */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: "#666", fontWeight: 600 }}>
                      Participants
                    </Typography>
                    <AvatarGroup max={6} sx={{ justifyContent: "center" }}>
                      {[...Array(Math.min(event.participants || 0, 6))].map((_, index) => (
                        <Avatar 
                          key={index}
                          src={`https://i.pravatar.cc/150?img=${index + 1}`}
                          sx={{ width: 40, height: 40, border: "2px solid white" }}
                        />
                      ))}
                    </AvatarGroup>
                    <Typography variant="caption" sx={{ color: "#666", mt: 1, display: "block", textAlign: "center" }}>
                      {event.participants || 0} people registered
                    </Typography>
                  </Box>

                  {/* Share Section */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: "#666", fontWeight: 600 }}>
                      Share this event
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      {["facebook", "x", "whatsapp", "email"].map((platform) => (
                        <IconButton
                          key={platform}
                          onClick={() => handleSharePlatform(platform)}
                          sx={{
                            background: "rgba(13, 42, 68, 0.08)",
                            color: THEME_BLUE,
                            "&:hover": {
                              background: "rgba(13, 42, 68, 0.16)",
                            },
                          }}
                          aria-label={`Share on ${platform}`}
                        >
                          {platform === "facebook" ? <FacebookRoundedIcon fontSize="small" /> : null}
                          {platform === "x" ? <XBrandIcon sx={{ fontSize: 18 }} /> : null}
                          {platform === "whatsapp" ? <WhatsAppIcon fontSize="small" /> : null}
                          {platform === "email" ? <EmailRoundedIcon fontSize="small" /> : null}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                </ContentCard>
              </Grid>
            </Grid>

            {/* Related Events - Optional Section */}
            {event.related_events && event.related_events.length > 0 && (
              <Box sx={{ mt: 8 }}>
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: "#333" }}>
                  You might also like
                </Typography>
                <Grid container spacing={3}>
                  {event.related_events.slice(0, 3).map((relatedEvent) => (
                    <Grid item xs={12} sm={6} md={4} key={relatedEvent.id}>
                      <Paper sx={{ 
                        p: 3, 
                        borderRadius: "16px",
                        border: "1px solid rgba(0,0,0,0.05)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                        }
                      }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: "#333" }}>
                          {relatedEvent.title}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                          {formatDateDDMMYYYY(relatedEvent.date)}
                        </Typography>
                        <Button
                          component={Link}
                          to={getEventDetailsPath(relatedEvent.id)}
                          size="small"
                          sx={{
                            color: "#667eea",
                            textTransform: "none",
                            fontWeight: 600,
                          }}
                        >
                          View Details →
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Container>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ 
            width: "100%",
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {!embedded ? (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      ) : null}
    </Box>
  );
}